import * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CreditCard,
  DollarSign,
  History,
  Key,
  Mail,
  MoreHorizontal,
  Palette,
  Pencil,
  Plug,
  Plug2,
  Server,
  ShieldCheck,
  ToggleLeft,
  ToggleRight,
  Trash2,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SurfaceEmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from "@ando/ui";
import { AndoBrainLoader } from "@ando/ui/blob";

import {
  useAdminOrg,
  useAdminOrgConnectors,
  useAdminOrgMembers,
} from "@/features/admin/hooks/useAdminOrg";

import { tierLabel } from "./_shared";
import { AddMemberDialog } from "./AddMemberDialog";
import { DeactivateOrgDialog } from "./DeactivateOrgDialog";
import { EditOrgDialog } from "./EditOrgDialog";
import { HardDeleteOrgDialog } from "./HardDeleteOrgDialog";
import { OrgOverviewTab } from "./OrgOverviewTab";
import { OrgMembersTab } from "./OrgMembersTab";
import { OrgInvitesTab } from "./OrgInvitesTab";
import { OrgConnectorsTab } from "./OrgConnectorsTab";
import { OrgBillingTab } from "./OrgBillingTab";
import { OrgCostTab } from "./OrgCostTab";
import { OrgBrandingTab } from "./OrgBrandingTab";
import { OrgInfrastructureTab } from "./OrgInfrastructureTab";
import { OrgSSOTab } from "./OrgSSOTab";
import { OrgSecurityTab } from "./OrgSecurityTab";
import { OrgAuditTab } from "./OrgAuditTab";

/** Compact KPI chip used in the header strip. */
function HeaderKpi({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 rounded-md border border-hairline bg-card/55 px-2.5 py-1.5">
      <span
        className="inline-flex h-5 w-5 items-center justify-center rounded text-accent"
        aria-hidden
      >
        {icon}
      </span>
      <span className="flex flex-col leading-tight">
        <span className="font-semibold text-[9px] text-ink-3 uppercase tracking-wide">
          {label}
        </span>
        <span className="font-medium text-[12.5px] text-ink-1 tabular-nums">
          {value}
        </span>
      </span>
    </div>
  );
}

/**
 * Organization detail — tabbed shell with 10 tabs.
 *
 * Owns: breadcrumb + tab bar. Each tab is lazy (TanStack Query only runs
 * when the tab mounts) so the initial load fetches ONLY ``org`` +
 * Overview KPIs; switching tabs triggers each domain query the first
 * time it's selected.
 *
 * Keyboard: Tabs primitive (Radix) handles ←/→ navigation natively.
 */

/**
 * Tab ids exposed via the URL (``?tab=...``). The ordered tuple drives:
 *   - the visual tab strip (left → right);
 *   - the route's ``validateSearch`` whitelist (importing this avoids
 *     a forked source of truth that drifts the moment a tab is added);
 *   - the ``OrgDetailTabId`` union below.
 *
 * Adding a new tab = one entry here. DRY.
 */
export const ORG_DETAIL_TAB_IDS = [
  "overview",
  "members",
  "invites",
  "connectors",
  "billing",
  "cost",
  "branding",
  "infrastructure",
  "sso",
  "security",
  "audit",
] as const;

export type OrgDetailTabId = (typeof ORG_DETAIL_TAB_IDS)[number];

interface TabDef {
  readonly id: OrgDetailTabId;
  readonly label: MessageDescriptor;
  readonly icon: typeof Building2;
}

/**
 * Macro-section grouping (June 2026 refactor): the 11 tabs are split into
 * 3 IA groups — Identità, Operatività, Sicurezza — to keep the bar
 * scannable. The URL contract (`?tab=<id>`) is unchanged so deep-links
 * from email / audit / Linear keep working; the macro section is
 * DERIVED from the active tab id (no second URL param needed).
 */
type OrgDetailSection = "identity" | "operativity" | "security";

interface SectionDef {
  readonly id: OrgDetailSection;
  readonly label: MessageDescriptor;
  readonly tabs: readonly TabDef[];
}

const ORG_DETAIL_SECTIONS: readonly SectionDef[] = [
  {
    id: "identity",
    label: msg`Identità`,
    tabs: [
      { id: "overview", label: msg`Overview`, icon: Building2 },
      { id: "branding", label: msg`Branding`, icon: Palette },
      { id: "members", label: msg`Membri`, icon: Users },
      { id: "invites", label: msg`Inviti`, icon: Mail },
    ],
  },
  {
    id: "operativity",
    label: msg`Operatività`,
    tabs: [
      { id: "connectors", label: msg`Connector`, icon: Plug },
      { id: "billing", label: msg`Billing`, icon: CreditCard },
      { id: "cost", label: msg`Costi`, icon: DollarSign },
      { id: "infrastructure", label: msg`Infrastruttura`, icon: Server },
    ],
  },
  {
    id: "security",
    label: msg`Sicurezza`,
    tabs: [
      { id: "sso", label: msg`SSO`, icon: Key },
      { id: "security", label: msg`Sicurezza`, icon: ShieldCheck },
      { id: "audit", label: msg`Audit`, icon: History },
    ],
  },
];

/** Reverse lookup: tab id → macro section it belongs to. */
const SECTION_BY_TAB: Readonly<Record<OrgDetailTabId, OrgDetailSection>> =
  ORG_DETAIL_SECTIONS.reduce(
    (acc, sec) => {
      for (const tab of sec.tabs) acc[tab.id] = sec.id;
      return acc;
    },
    {} as Record<OrgDetailTabId, OrgDetailSection>,
  );

export interface OrgDetailShellProps {
  orgId: string;
}

export function OrgDetailShell({
  orgId,
}: OrgDetailShellProps): React.ReactElement {
  const { i18n } = useLingui();
  const orgQuery = useAdminOrg(orgId);
  // Header KPI counters: members and licensed connectors. We surface them
  // INSIDE the shell so they're visible regardless of the active tab; the
  // Overview tab keeps its own (richer) KPI grid for the canonical view.
  const membersQuery = useAdminOrgMembers(orgId);
  const connectorsQuery = useAdminOrgConnectors(orgId);

  // Lifecycle dialog state. Each piece of state opens exactly ONE dialog;
  // they're never composed (no nested confirms) so a plain boolean tuple
  // is clearer than a discriminated union here.
  const [editOpen, setEditOpen] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = React.useState(false);
  const [addMemberOpen, setAddMemberOpen] = React.useState(false);

  // URL-driven tab state — deep links to ``?tab=members`` work, refresh
  // preserves the active tab, browser back navigates between tabs as
  // discrete history steps. ``replace: true`` keeps the LIST → DETAIL
  // jump as the one history entry that matters; tab switching after
  // landing on the detail page does NOT pollute back-button stack.
  const navigate = useNavigate();
  const { tab: activeTab } = useSearch({
    from: "/_authed/_modal/admin/orgs/$orgId",
  });
  const setActiveTab = React.useCallback(
    (next: OrgDetailTabId): void => {
      void navigate({
        to: "/admin/orgs/$orgId",
        params: { orgId },
        search: { tab: next },
        replace: true,
      });
    },
    [navigate, orgId],
  );

  if (orgQuery.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center px-8">
        <AndoBrainLoader size="md" label={i18n._(msg`Caricamento organizzazione…`)} />
      </div>
    );
  }

  if (orgQuery.isError || !orgQuery.data) {
    return (
      <div className="flex h-full w-full items-center justify-center px-8">
        <SurfaceEmptyState
          icon={<Building2 size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Organizzazione non trovata`)}
          description={
            orgQuery.error instanceof Error
              ? orgQuery.error.message
              : i18n._(msg`Verifica l'ID e riprova.`)
          }
        />
      </div>
    );
  }

  const org = orgQuery.data;
  const tierName = tierLabel(org.usage_multiplier);
  const memberCount = membersQuery.data?.length ?? org.members_count ?? null;
  const licensedConnectors =
    connectorsQuery.data?.filter((c) => c.is_enabled).length ?? null;

  return (
    <div className="flex h-full w-full flex-col">
      {/* Breadcrumb header */}
      <header
        className="flex flex-col gap-3 border-hairline border-b bg-cream/30 px-8 pt-7 pb-5"
        aria-label={i18n._(msg`Dettaglio organizzazione`)}
      >
        <div className="flex items-center gap-3">
          <Link
            to="/admin/orgs"
            aria-label={i18n._(msg`Torna alla lista organizzazioni`)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-2 transition-colors hover:bg-ando-orange-soft/40 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <ArrowLeft size={16} aria-hidden />
          </Link>
          <nav
            aria-label={i18n._(msg`Briciole di pane`)}
            className="flex items-center gap-2 text-[12px] text-ink-3"
          >
            <Link to="/admin/orgs" className="hover:text-ink-2">
              {i18n._(msg`Organizzazioni`)}
            </Link>
            <span aria-hidden>/</span>
            <span className="text-ink-2">{org.name}</span>
          </nav>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <div className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
              {i18n._(msg`Admin · Organizzazione`)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate font-[550] font-display text-[26px] text-ink-1 leading-tight tracking-[-0.028em]">
                {org.name}
              </h1>
              <span className="inline-flex items-center gap-1 rounded-sm bg-ando-orange-soft/60 px-2 py-0.5 font-medium text-[11px] text-accent">
                ×{org.usage_multiplier} · {tierName}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-ink-3">
              <span>{org.slug}</span>
              <span aria-hidden>·</span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-sm px-2 py-0.5",
                  org.is_active
                    ? "bg-success/15 text-success-foreground"
                    : "bg-destructive/15 text-destructive",
                )}
              >
                {org.is_active
                  ? i18n._(msg`Attiva`)
                  : i18n._(msg`Disattivata`)}
              </span>
            </div>
          </div>

          {/* Right rail: primary actions + overflow menu */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAddMemberOpen(true)}
            >
              <UserPlus size={13} className="mr-1.5" aria-hidden />
              {i18n._(msg`Aggiungi membro`)}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  aria-label={i18n._(msg`Altre azioni`)}
                >
                  <MoreHorizontal size={14} aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                  <Pencil size={13} className="mr-2" aria-hidden />
                  {i18n._(msg`Modifica`)}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setDeactivateOpen(true)}>
                  {org.is_active ? (
                    <>
                      <ToggleLeft size={13} className="mr-2" aria-hidden />
                      {i18n._(msg`Disattiva`)}
                    </>
                  ) : (
                    <>
                      <ToggleRight size={13} className="mr-2" aria-hidden />
                      {i18n._(msg`Riattiva`)}
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setHardDeleteOpen(true)}
                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                >
                  <Trash2 size={13} className="mr-2" aria-hidden />
                  {i18n._(msg`Elimina definitivamente`)}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* KPI strip */}
        <div
          className="flex flex-wrap items-center gap-2"
          aria-label={i18n._(msg`Indicatori organizzazione`)}
        >
          <HeaderKpi
            icon={<Users size={11} aria-hidden />}
            label={i18n._(msg`Membri`)}
            value={
              memberCount === null
                ? "—"
                : org.max_members === -1
                  ? `${memberCount}`
                  : `${memberCount} / ${org.max_members}`
            }
          />
          <HeaderKpi
            icon={<Plug2 size={11} aria-hidden />}
            label={i18n._(msg`Connector`)}
            value={licensedConnectors === null ? "—" : licensedConnectors}
          />
          <HeaderKpi
            icon={<Zap size={11} aria-hidden />}
            label={i18n._(msg`Tier`)}
            value={`×${org.usage_multiplier}`}
          />
          <HeaderKpi
            icon={<Calendar size={11} aria-hidden />}
            label={i18n._(msg`Creata`)}
            value={new Date(org.created_at).toLocaleDateString("it-IT")}
          />
        </div>
      </header>

      {/* Tab bar — 2-tier (macro section → sub-tabs).
          Active section is derived from the URL ?tab=. Clicking a macro
          section jumps to its first sub-tab so the deep-link stays valid. */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as OrgDetailTabId)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex flex-col border-hairline border-b bg-cream/20 px-8">
          {/* Macro section bar */}
          <div
            role="tablist"
            aria-label={i18n._(msg`Macro-sezioni`)}
            className="flex items-center gap-1 pt-2"
          >
            {ORG_DETAIL_SECTIONS.map((section) => {
              const isActive = SECTION_BY_TAB[activeTab] === section.id;
              const firstTab = section.tabs[0];
              return (
                <button
                  key={section.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => firstTab && setActiveTab(firstTab.id)}
                  className={cn(
                    "inline-flex items-center rounded-full px-3 py-[5px] font-medium text-[11px] leading-none transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-1",
                    isActive
                      ? "bg-ink-1 text-white shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                      : "bg-transparent text-ink-2 hover:bg-card/60 hover:text-ink-1",
                  )}
                >
                  {i18n._(section.label)}
                </button>
              );
            })}
          </div>
          {/* Sub-tab strip — only the sub-tabs of the active macro section */}
          <TabsList
            aria-label={i18n._(msg`Sezioni dettaglio organizzazione`)}
            className="h-auto justify-start gap-1 bg-transparent p-0 py-2"
          >
            {(
              ORG_DETAIL_SECTIONS.find(
                (s) => s.id === SECTION_BY_TAB[activeTab],
              )?.tabs ?? []
            ).map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px]"
                >
                  <Icon size={13} aria-hidden />
                  {i18n._(t.label)}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-card/40 px-8 py-6">
          <TabsContent value="overview" className="mt-0">
            <OrgOverviewTab org={org} />
          </TabsContent>
          <TabsContent value="members" className="mt-0">
            {activeTab === "members" && <OrgMembersTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="invites" className="mt-0">
            {activeTab === "invites" && <OrgInvitesTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="connectors" className="mt-0">
            {activeTab === "connectors" && <OrgConnectorsTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="billing" className="mt-0">
            {activeTab === "billing" && <OrgBillingTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="cost" className="mt-0">
            {activeTab === "cost" && <OrgCostTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="branding" className="mt-0">
            {activeTab === "branding" && (
              <OrgBrandingTab orgId={orgId} orgName={org.name} />
            )}
          </TabsContent>
          <TabsContent value="infrastructure" className="mt-0">
            {activeTab === "infrastructure" && (
              <OrgInfrastructureTab orgId={orgId} />
            )}
          </TabsContent>
          <TabsContent value="sso" className="mt-0">
            {activeTab === "sso" && <OrgSSOTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="security" className="mt-0">
            {activeTab === "security" && <OrgSecurityTab orgId={orgId} />}
          </TabsContent>
          <TabsContent value="audit" className="mt-0">
            {activeTab === "audit" && <OrgAuditTab orgId={orgId} />}
          </TabsContent>
        </div>
      </Tabs>

      {/* Lifecycle dialogs — mounted once at the shell level so they share
          the same focus-trap stacking order regardless of the active tab. */}
      <EditOrgDialog open={editOpen} onOpenChange={setEditOpen} org={org} />
      <DeactivateOrgDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        org={org}
      />
      <HardDeleteOrgDialog
        open={hardDeleteOpen}
        onOpenChange={setHardDeleteOpen}
        org={org}
      />
      <AddMemberDialog
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        org={org}
      />
    </div>
  );
}
